import React, { FC, useContext, useEffect, useRef } from 'react';
import { QuestionnaireContext } from './QuestionnaireContextProvider';
import { useShowConfirmationPopup } from '../ConfirmationPopup';
import { useHistory } from 'react-router';
import { Operation, OperationHistorySuspendOperation, useApiDispatchQueue } from '../../..';
import { v4 as uuidv4 } from 'uuid';
import * as AuthStore from '../../store/AuthStore';
import { ApiDispatchItem } from '../../../state/ApiDispatchState';

export const QuestionnaireNavigationBlocker: FC = ({ children }) => {
  const history = useHistory();
  const dispatchApiCall = useApiDispatchQueue();
  const showConfirmationPopup = useShowConfirmationPopup();
  const questionnaireState = useContext(QuestionnaireContext);
  const {
    questionnairePageVisible,
    concludingNotes,
    baseQuestionsState,
    baseQuestionsAnswered,
    additionalQuestionsState,
    additionalQuestionsAnswered,
    notes,
    concludingQuestionsState,
    concludingQuestionsAnswered,
    operation, setOperation,
    tmpOperation,
    getDefaultOperation,
    logAction,
    questionnaireBase,
    questionnaireConcludingQuestions,
    loadStateFromOperation,
  } = questionnaireState;

  const unblock = useRef(null);
  useEffect(() => {
    if (unblock.current) {
      unblock.current();
    }
    // @ts-ignore
    unblock.current = history.block((location) => {
      const allowedRoutes = [
        /\/add-document/,
        /\/products/,
        /\/products\/.+/,
      ];
      const navigateToAllowedRoute = allowedRoutes.some((regex) => (location?.pathname || '').match(regex));
      if (operation?.id && !location?.pathname?.endsWith(operation?.id) && !navigateToAllowedRoute) {
        // confirmation dialog
        showConfirmationPopup({
          open: true,
          actionButtonLabel: 'Unterbrechen',
          closeFn: () => {
            showConfirmationPopup(null);
          },
          actionFn: (close) => {
            // save current state
            suspendQuestionniareAndSaveState();
            // reset questionnaire data
            loadStateFromOperation(null);

            // navigate
            unblock.current();
            history.push(location);

            // close
            close();
          },
          renderer: () => {
            return <>Möchten Sie die aktuelle Bedarfsanalyse wirklich unterbrechen?</>;
          },
        });
        return false;
      }
      // unblock navigation
      unblock.current();
      if (!navigateToAllowedRoute) {
        // reset questionnaire data
        loadStateFromOperation(null);
      }
    });
  }, [questionnaireState]);

  const suspendQuestionniareAndSaveState = () => {
    // prepare nextOperation object
    let nextOperation = {
      ...(tmpOperation || getDefaultOperation()),
      questions: Object.keys(baseQuestionsState[0]).map((questionId) => {
        const answer = baseQuestionsState[0][questionId];
        const question = questionnaireBase?.questionnaire?.questions?.find(({ id }) => id === questionId);
        return {
          id: questionId,
          label: question?.label || '',
          type: question?.type || '',
          required: question?.required || false,
          value: answer,
          answered: baseQuestionsAnswered.includes(questionId),
        };
      }),
      additionalQuestions: Object.keys(additionalQuestionsState[0]).reduce((res, key) => ({
        ...res,
        [key]: (
          Object.keys(additionalQuestionsState[0][key] || {}).map((questionId) => {
            const answer = additionalQuestionsState[0][key][questionId];
            const question = questionnaireBase?.questionnaire?.additionalQuestions?.find(({ id }) => id === questionId);
            return {
              id: questionId,
              label: question?.label || '',
              type: question?.type || '',
              required: question?.required || false,
              value: answer,
              answered: (additionalQuestionsAnswered[key] || []).includes(questionId),
            };
          })
        ),
      }), {}),
      concludingQuestions: Object.keys(concludingQuestionsState[0]).map((questionId) => {
        const answer = concludingQuestionsState[0][questionId];
        const question = questionnaireConcludingQuestions?.questionnaire?.questions?.find(({ id }) => id === questionId);
        return {
          id: questionId,
          label: question?.label || '',
          type: question?.type || '',
          required: question?.required || false,
          value: answer,
          answered: concludingQuestionsAnswered.includes(questionId),
        };
      }),
      notes: notes?.list || [],
    } as Operation;

    // add concluding notes
    if (concludingNotes?.trim().length > 0) {
      nextOperation.notes = [...nextOperation.notes.filter(({ subject }) => subject !== 'Zusätzliche Anmerkungen'), {
        subject: 'Zusätzliche Anmerkungen',
        body: concludingNotes,
        timestamp: new Date().toISOString(),
      }];
    }

    // log operation suspend
    nextOperation = logAction({
      type: 'SUSPEND_OPERATION',
    } as OperationHistorySuspendOperation, nextOperation);

    // update operation
    let operationId = operation?.id || null;
    let createOperation = false;
    if (!operationId) {
      operationId = `dependency_${uuidv4()}`;
      createOperation = true;
      setOperation({
        id: operationId,
      } as any);
    }
    dispatchApiCall({
      method: createOperation ? 'post' : 'put',
      route: `/api/operations/${createOperation ? 'create': `${operationId}/update`}`,
      body: { ...nextOperation, id: operationId },
      header: AuthStore.getAuthHeader(),
      onSuccess: (res) => {
        // console.log('operation response', res.data, JSON.stringify(res.data));
        const op = res.data as Operation;
        if (typeof op === 'object' && op?.id && questionnairePageVisible.current) {
          setOperation(op);
        }
      },
    } as ApiDispatchItem);
  }

  return <>{ children }</>;
}
